import React, { useMemo, useState } from "react";
import { Alert, Button, Form, Pagination } from "react-bootstrap";
import "./AdherentShowcase.css";

const PAGE_SIZE = 6;

function defaultDateGetter(item) {
  return item.created_at || item.date_debut || item.updated_at || "";
}

export default function AdherentShowcase({
  title,
  subtitle,
  items,
  eyebrow = "Nouveautes",
  searchPlaceholder = "Rechercher...",
  emptyText = "Aucun element disponible.",
  getDate = defaultDateGetter,
  getSearchText,
  getStatus,
  statusOptions = [],
  showCarousel = true,
  showArchive = true,
  archiveIncludesNewest = false,
  archiveTitle = "Tous les anciens elements",
  renderHero,
  renderArchiveRow,
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);

  const sortedItems = useMemo(
    () =>
      [...items].sort((a, b) => {
        const da = new Date(getDate(a) || 0).getTime();
        const db = new Date(getDate(b) || 0).getTime();
        return db - da;
      }),
    [items, getDate]
  );

  const newest = sortedItems.slice(0, 3);
  const archive = archiveIncludesNewest ? sortedItems : sortedItems.slice(3);

  const filteredArchive = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return archive.filter((item) => {
      const text = (getSearchText ? getSearchText(item) : JSON.stringify(item)).toLowerCase();
      const itemStatus = getStatus ? getStatus(item) : "all";
      const matchesQuery = !normalizedQuery || text.includes(normalizedQuery);
      const matchesStatus = status === "all" || itemStatus === status;
      return matchesQuery && matchesStatus;
    });
  }, [archive, getSearchText, getStatus, query, status]);

  const totalPages = Math.max(1, Math.ceil(filteredArchive.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filteredArchive.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const goTo = (nextIndex) => {
    if (newest.length === 0) return;
    const safeIndex = (nextIndex + newest.length) % newest.length;
    setActiveIndex(safeIndex);
  };

  return (
    <section className="showcase">
      <div className="showcase-heading">
        <div>
          <span className="showcase-eyebrow">{eyebrow}</span>
          <h2>{title}</h2>
          {subtitle && <p>{subtitle}</p>}
        </div>
      </div>

      {items.length === 0 ? (
        <Alert variant="warning">{emptyText}</Alert>
      ) : (
        <>
          {showCarousel && (
          <div className="showcase-carousel">
            {newest.length > 0 ? (
              <>
                <div className="showcase-slide">
                  {renderHero(newest[activeIndex], activeIndex)}
                </div>

                <div className="showcase-controls">
                  <Button variant="light" onClick={() => goTo(activeIndex - 1)} disabled={newest.length <= 1}>
                    Precedent
                  </Button>
                  <div className="showcase-dots">
                    {newest.map((item, index) => (
                      <button
                        key={item.id || index}
                        type="button"
                        className={index === activeIndex ? "active" : ""}
                        onClick={() => setActiveIndex(index)}
                        aria-label={`Voir element ${index + 1}`}
                      />
                    ))}
                  </div>
                  <Button variant="light" onClick={() => goTo(activeIndex + 1)} disabled={newest.length <= 1}>
                    Suivant
                  </Button>
                </div>
              </>
            ) : (
              <Alert variant="warning" className="mb-0">{emptyText}</Alert>
            )}
          </div>
          )}

          {showArchive && (
          <div className="showcase-archive">
            <div className="showcase-archive-head">
              <div>
                <span className="showcase-eyebrow">Archive</span>
                <h3>{archiveTitle}</h3>
              </div>
              <div className="showcase-filters">
                <Form.Control
                  value={query}
                  placeholder={searchPlaceholder}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setPage(1);
                  }}
                />
                {statusOptions.length > 0 && (
                  <Form.Select
                    value={status}
                    onChange={(e) => {
                      setStatus(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="all">Tous les statuts</option>
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                )}
              </div>
            </div>

            {archive.length === 0 ? (
              <Alert variant="light" className="mb-0">
                Les archives apparaitront ici apres les 3 plus recents.
              </Alert>
            ) : pageItems.length === 0 ? (
              <Alert variant="warning" className="mb-0">
                Aucun resultat avec ces filtres.
              </Alert>
            ) : (
              <>
                <div className="showcase-archive-list">
                  {pageItems.map((item) => renderArchiveRow(item))}
                </div>

                {totalPages > 1 && (
                  <Pagination className="showcase-pagination">
                    <Pagination.Prev
                      disabled={currentPage <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    />
                    {Array.from({ length: totalPages }, (_, index) => index + 1).map((p) => (
                      <Pagination.Item key={p} active={p === currentPage} onClick={() => setPage(p)}>
                        {p}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next
                      disabled={currentPage >= totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    />
                  </Pagination>
                )}
              </>
            )}
          </div>
          )}
        </>
      )}
    </section>
  );
}
